import { AMADEUS, getHotelLoyaltyProgram } from "@capone/common";
import { Box, Button, Divider, Tooltip, Typography } from "@material-ui/core";
import { Add } from "@material-ui/icons";
import clsx from "clsx";
import {
  Icon,
  IconName,
  LoyaltyInfoFormComponentProps,
  TravelerSelectDropdown,
  TravelerSelectStep,
} from "halifax";
import React, { Fragment, useEffect, useState } from "react";
import { CorpRoomProduct, IPerson, ITravelerStepErrors } from "redmond";
import { LoyaltyInformationForm } from "../LoyaltyInformationForm";
import {
  MULTIROOM_TOOLTIP_TEXT,
  MULTIROOM_TOOLTIP_TITLE,
  TRAVELER_SELECT_DROPDOWN_DESCRIPTION,
} from "./constants";
import "./styles.scss";

export interface HotelTravelerSelectDropdownFormProps {
  chosenProduct: CorpRoomProduct;
  roomsCount: number;
  travelers: IPerson[];
  disableEditTravelerInfo: boolean;
  selectedTravelers: IPerson[];
  updateUserPassenger: (passenger: IPerson, isNewTraveler: boolean) => void;
  updateSelectedTravelersList: (traveler: IPerson) => void;
  removeSelectedTraveler: (traveler: IPerson) => void;
  setProgress: (progress: TravelerSelectStep) => void;
  showErrors: ITravelerStepErrors;
  setShowErrors: (showErrors: ITravelerStepErrors) => void;
  showRemove?: boolean;
  loading?: boolean;
}

const getRenderLoyaltyComponent =
  (
    chosenProduct: CorpRoomProduct,
    showErrors: ITravelerStepErrors,
    setShowErrors: (errors: ITravelerStepErrors) => void
  ) =>
  ({ onChange, disabled, traveler }: LoyaltyInfoFormComponentProps) =>
    (
      <LoyaltyInformationForm
        chosenProduct={chosenProduct}
        onChange={(newLoyaltyNumber) => onChange(newLoyaltyNumber)}
        disabled={disabled}
        traveler={traveler}
        setShowLoyaltyError={(isError) =>
          setShowErrors?.({
            ...showErrors,
            corpPrimaryTraveler: isError,
          })
        }
      />
    );

export const HotelTravelerSelectDropdownForm = ({
  chosenProduct,
  roomsCount,
  travelers,
  disableEditTravelerInfo,
  selectedTravelers,
  updateSelectedTravelersList,
  updateUserPassenger,
  setProgress,
  removeSelectedTraveler,
  showErrors,
  setShowErrors,
  showRemove,
  loading,
}: HotelTravelerSelectDropdownFormProps) => {
  const [dropdownCount, setDropdownCount] = useState(
    selectedTravelers.length || 1
  );

  useEffect(() => {
    if (disableEditTravelerInfo) {
      setDropdownCount(selectedTravelers.length);
    }
  }, [disableEditTravelerInfo]);

  const isLoyaltyEligible = Boolean(chosenProduct?.loyaltyProgramCode);
  const loyaltyProgram = chosenProduct?.loyaltyProgramCode
    ? getHotelLoyaltyProgram(chosenProduct?.loyaltyProgramCode)
    : null;

  const isAmadeusProvider = chosenProduct?.providerName === AMADEUS;

  return (
    <div
      className={clsx("hotels-traveler-select-dropdown-container", {
        loading: !!loading,
      })}
    >
      <div className="form-container">
        <Typography variant="h3" className="title">
          Select or add travelers
        </Typography>
        <Typography variant="body2" component="p" color="textPrimary">
          {TRAVELER_SELECT_DROPDOWN_DESCRIPTION(
            roomsCount,
            isAmadeusProvider,
            true
          )}
        </Typography>
        {[...Array(dropdownCount)].map((_, index) => (
          <Fragment key={index}>
            {index > 0 && <Divider style={{ margin: "20px 0" }} />}
            <TravelerSelectDropdown
              travelers={travelers}
              disableEditTravelerInfo={disableEditTravelerInfo}
              selectedTravelers={selectedTravelers}
              updateSelectedTraveler={updateSelectedTravelersList}
              showRemove={showRemove}
              removeSelectedTraveler={(traveler) => {
                removeSelectedTraveler(traveler);
                setDropdownCount((prevCount) =>
                  prevCount === 1 ? 1 : prevCount - 1
                );
              }}
              loading={!!loading}
              setProgress={setProgress}
              index={index}
              updateUserPassenger={(payload, isNewTraveler) =>
                updateUserPassenger(payload.person, isNewTraveler ?? false)
              }
              showErrors={showErrors}
              setShowErrors={setShowErrors}
              loyaltyProgram={loyaltyProgram}
              renderLoyaltyComponent={
                isLoyaltyEligible
                  ? getRenderLoyaltyComponent(chosenProduct, showErrors, setShowErrors)
                  : undefined
              }
              productType="hotel"
            />
          </Fragment>
        ))}
        {isAmadeusProvider && roomsCount > 1 && dropdownCount < roomsCount && (
          <Button
            variant="outlined"
            color="secondary"
            className="add-traveler-button"
            startIcon={<Add />}
            disabled={
              dropdownCount > Object.keys(selectedTravelers).length ||
              disableEditTravelerInfo
            }
            onClick={() => setDropdownCount((prevCount) => prevCount + 1)}
          >
            Add additional traveler
          </Button>
        )}
        {!isAmadeusProvider && roomsCount > 1 && (
          <Tooltip
            title={
              <Typography variant="subtitle2">
                {MULTIROOM_TOOLTIP_TEXT}
              </Typography>
            }
            placement="top"
            tabIndex={0}
          >
            <Box
              display="flex"
              className="multiroom-tooltip"
              alignItems="center"
              gridGap="6px"
            >
              <Typography variant="subtitle2">
                {MULTIROOM_TOOLTIP_TITLE}
              </Typography>
              <Icon name={IconName.InfoCircle} />
            </Box>
          </Tooltip>
        )}
      </div>
    </div>
  );
};
